/**
 * apps/web/src/components/OfflineCache.tsx
 *
 * Wires React Query cache persistence to the auth lifecycle. Mounted under
 * AuthProvider so the persisted cache is keyed (busted) by the active user +
 * workspace. While authenticated, the cache is restored + kept in sync; on
 * logout it is purged so the next user on a shared device can't see stale data.
 *
 * Uses the imperative core API (persistQueryClient) rather than
 * PersistQueryClientProvider so the session-derived buster is available without a
 * provider-mount-ordering conflict with AuthProvider.
 */

import type React from 'react';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { useAuth } from '../auth/AuthContext';
import { createWebPersister, sessionCacheBuster, purgePersistedCache } from '../lib/persistence';

const persister = createWebPersister();
const MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24h — persisted reads are labelled-stale, not authoritative

export function OfflineCache({ children }: { children: React.ReactNode }): React.ReactElement {
  const queryClient = useQueryClient();
  const { status, user, workspace } = useAuth();
  const userId = user?.id ?? null;
  const workspaceId = workspace?.id ?? null;

  useEffect(() => {
    if (status === 'unauthenticated') {
      // Wipe any persisted cache so it can't leak to the next user on this device.
      void purgePersistedCache();
      return;
    }
    if (status !== 'authenticated' || !userId || !workspaceId) {
      return; // still loading — don't restore until we know whose cache it is
    }
    const [unsubscribe] = persistQueryClient({
      queryClient,
      persister,
      maxAge: MAX_AGE_MS,
      buster: sessionCacheBuster(userId, workspaceId),
    });
    return unsubscribe;
  }, [status, userId, workspaceId, queryClient]);

  return <>{children}</>;
}
